import { Pencil, Tag } from "lucide-react";
import {
  useCreate,
  useDelete,
  useGetList,
  useLocaleState,
  useNotify,
  useTranslate,
  useUpdate,
  type Identifier,
} from "ra-core";
import { useEffect, useId, useState, type FormEvent } from "react";

import { ExternalAnchor } from "@/components/market-intel/components";
import {
  Field,
  FieldError,
  FormDialog,
  SELECT_CLASS,
} from "@/components/market-intel/FormDialog";
import { MiniMarkdown } from "@/components/market-intel/MiniMarkdown";
import type { MarketCompetitor } from "@/components/market-intel/types";
import { hostOf, riseStyle } from "@/components/market-intel/util";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { formatMoney } from "@/lib/money";

import { amountInput } from "./forms";
import {
  BILLING_PERIODS,
  billingPeriodLabelKey,
  normalizeProductForm,
  productFormDefaults,
  sortProducts,
  type ProductFormValues,
} from "./intel";
import type { BillingPeriod, CompetitorProduct } from "./types";
import {
  AddButton,
  ConfirmDeleteButton,
  FormActions,
  IconAction,
  SectionCard,
} from "./ui";

/** "/mes", "/año", "pago único" after a price. */
const perKey = (period: BillingPeriod): string =>
  `competition.products.per.${period === "año" ? "ano" : period}`;

/** The competitor's plans and prices, cheapest first. */
export const ProductsSection = ({
  id,
  competitor,
}: {
  id: string;
  competitor: MarketCompetitor;
}) => {
  const translate = useTranslate();
  const [editing, setEditing] = useState<CompetitorProduct | "new" | null>(
    null,
  );
  const { data } = useGetList<CompetitorProduct>("competitor_products", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "price", order: "ASC" },
    filter: { competitor_id: competitor.id },
  });
  const products = sortProducts(data ?? []);

  return (
    <SectionCard
      id={id}
      icon={Tag}
      title={translate("competition.products.heading")}
      count={products.length || undefined}
      style={riseStyle(2)}
      action={
        <AddButton
          label={translate("competition.products.add")}
          onClick={() => setEditing("new")}
        />
      }
    >
      {products.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {competitor.entry_price
            ? `${translate("market.competitors.entry_price")}: ${competitor.entry_price}`
            : translate("competition.products.empty")}
        </p>
      ) : (
        <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
          {products.map((product) => (
            <ProductTile
              key={product.id}
              product={product}
              onEdit={() => setEditing(product)}
            />
          ))}
        </ul>
      )}
      <ProductForm
        open={editing != null}
        onOpenChange={(open) => !open && setEditing(null)}
        competitorId={competitor.id}
        product={editing === "new" ? null : editing}
      />
    </SectionCard>
  );
};

const ProductTile = ({
  product,
  onEdit,
}: {
  product: CompetitorProduct;
  onEdit: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale = "es"] = useLocaleState();
  const [remove, { isPending }] = useDelete<CompetitorProduct>();
  const per =
    product.billing_period && product.billing_period !== "otro"
      ? translate(perKey(product.billing_period))
      : "";
  return (
    <li className="flex min-w-0 flex-col gap-1 rounded-2xl bg-muted/50 p-3 dark:bg-white/5">
      <div className="flex min-w-0 items-start gap-1">
        <h3 className="min-w-0 flex-1 pt-1.5 text-sm font-semibold wrap-anywhere">
          {product.name}
        </h3>
        <IconAction
          label={`${translate("competition.products.edit")}: ${product.name}`}
          icon={Pencil}
          onClick={onEdit}
          className="-mt-0.5"
        />
        <ConfirmDeleteButton
          label={`${translate("competition.form.delete")}: ${product.name}`}
          disabled={isPending}
          className="-mt-0.5 -mr-1"
          onConfirm={() =>
            remove(
              "competitor_products",
              { id: product.id, previousData: product },
              {
                mutationMode: "optimistic",
                onSuccess: () =>
                  notify("competition.form.deleted", { type: "info" }),
                onError: () =>
                  notify("competition.form.error", { type: "error" }),
              },
            )
          }
        />
      </div>
      <p className="text-xl leading-tight font-bold tracking-tight tabular-nums">
        {product.price != null ? (
          <>
            {formatMoney(product.price, locale, product.currency)}
            {per ? (
              <span className="ml-1 text-sm font-semibold text-muted-foreground">
                {per}
              </span>
            ) : null}
          </>
        ) : (
          <span className="text-sm font-semibold text-muted-foreground">
            {translate("competition.products.no_price")}
          </span>
        )}
      </p>
      {product.description ? (
        <MiniMarkdown
          source={product.description}
          className="text-sm text-muted-foreground"
        />
      ) : null}
      {product.url ? (
        <ExternalAnchor href={product.url} className="text-sm">
          {hostOf(product.url)}
        </ExternalAnchor>
      ) : null}
    </li>
  );
};

const ProductForm = ({
  open,
  onOpenChange,
  competitorId,
  product,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  competitorId: Identifier;
  product: CompetitorProduct | null;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale = "es"] = useLocaleState();
  const formId = useId();
  const [values, setValues] = useState(() => productFormDefaults());
  const [error, setError] = useState<string | null>(null);
  const [create, { isPending: creating }] = useCreate();
  const [update, { isPending: updating }] = useUpdate();

  useEffect(() => {
    if (open) {
      setValues(
        productFormDefaults(product ?? undefined, (value) =>
          amountInput(value, locale),
        ),
      );
      setError(null);
    }
  }, [open, product, locale]);

  const set =
    <K extends keyof ProductFormValues>(key: K) =>
    (value: ProductFormValues[K]) => {
      setValues((current) => ({ ...current, [key]: value }));
      setError(null);
    };
  const close = () => onOpenChange(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const result = normalizeProductForm(competitorId, values);
    if (!result.ok) return setError(result.error);
    const options = {
      onSuccess: () => {
        notify("competition.form.saved", { type: "success" });
        close();
      },
      onError: () => notify("competition.form.error", { type: "error" }),
    };
    if (product)
      update(
        "competitor_products",
        { id: product.id, data: result.data, previousData: product },
        options,
      );
    else create("competitor_products", { data: result.data }, options);
  };

  const errorId = `${formId}-error`;
  const field = (key: "name" | "price" | "currency" | "url") => (
    <Field
      id={`${formId}-${key}`}
      label={translate(`competition.products.form.${key}`)}
    >
      <Input
        id={`${formId}-${key}`}
        value={values[key]}
        onChange={(event) => set(key)(event.target.value)}
        inputMode={
          key === "price" ? "decimal" : key === "url" ? "url" : undefined
        }
        type={key === "url" ? "url" : "text"}
        maxLength={key === "currency" ? 3 : undefined}
        aria-describedby={error ? errorId : undefined}
      />
    </Field>
  );
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={translate(
        product ? "competition.products.edit" : "competition.products.add",
      )}
      formId={formId}
      onSubmit={submit}
      actions={
        <FormActions
          formId={formId}
          disabled={creating || updating}
          onCancel={close}
        />
      }
    >
      {field("name")}
      <div className="grid grid-cols-[minmax(0,1fr)_5.5rem] gap-3 sm:grid-cols-[minmax(0,1fr)_6rem_minmax(0,1fr)]">
        {field("price")}
        {field("currency")}
        <Field
          id={`${formId}-period`}
          label={translate("competition.products.form.billing_period")}
          className="max-sm:col-span-2"
        >
          <select
            id={`${formId}-period`}
            className={SELECT_CLASS}
            value={values.billing_period}
            onChange={(event) =>
              set("billing_period")(
                event.target.value as ProductFormValues["billing_period"],
              )
            }
          >
            <option value="">
              {translate("competition.products.periods.none")}
            </option>
            {BILLING_PERIODS.map((period) => (
              <option key={period} value={period}>
                {translate(billingPeriodLabelKey(period))}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {error ? <FieldError id={errorId}>{translate(error)}</FieldError> : null}
      <Field
        id={`${formId}-description`}
        label={translate("competition.products.form.description")}
      >
        <Textarea
          id={`${formId}-description`}
          value={values.description}
          onChange={(event) => set("description")(event.target.value)}
          rows={3}
        />
      </Field>
      {field("url")}
    </FormDialog>
  );
};
